import { resolveShopifyCardPipelineLabelFromProgress, resolveShopifyPipelineStage } from './shopifyPipelineStages';
import { pickShopifyDisplayPercent } from './shopifyPipelineProgress';
import { loadingTitleForInternalPipelineStage } from './uploadProgress';

const ACTIVE_UPLOAD_PHASES = new Set(['uploading', 'pipeline', 'queueing']);
const STICKY_PROCESSING_STORAGE_KEY = 'volugraph_shopify_sticky_processing_v1';
/** Drop stale sticky rows that outlive the tab session or no longer match the catalogue. */
const STICKY_PROCESSING_MAX_AGE_MS = 12 * 60 * 60 * 1000;

export function isShopifyUploadInProgress(uploadPhase) {
  return ACTIVE_UPLOAD_PHASES.has(uploadPhase);
}

function readStickyProcessingMap() {
  try {
    const raw = sessionStorage.getItem(STICKY_PROCESSING_STORAGE_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === 'object' ? parsed : {};
  } catch {
    return {};
  }
}

function writeStickyProcessingMap(map) {
  try {
    sessionStorage.setItem(STICKY_PROCESSING_STORAGE_KEY, JSON.stringify(map));
  } catch {
    /* ignore quota / private mode */
  }
}

/** Remember in-flight Shopify product ↔ capture link across UploadContext clears / modal navigation. */
export function persistShopifyProcessingEntry(productId, { captureId, projectId } = {}) {
  const pid = String(productId || '').trim();
  const cid = String(captureId || '').trim();
  const projId = String(projectId || '').trim();
  if (!pid || !cid || !projId) return;
  const map = readStickyProcessingMap();
  map[pid] = { captureId: cid, projectId: projId, updatedAt: Date.now() };
  writeStickyProcessingMap(map);
}

export function removeShopifyProcessingEntry(productId) {
  const pid = String(productId || '').trim();
  if (!pid) return;
  const map = readStickyProcessingMap();
  if (!map[pid]) return;
  delete map[pid];
  writeStickyProcessingMap(map);
}

/** Reset a catalogue row to the default “no 3D viewer” state after unlink/delete. */
export function resetShopifyProductToDefault(product) {
  return {
    ...product,
    state: 'none',
    captureId: null,
    projectId: null,
    shareToken: null,
    progressPercent: null,
    pipelineStageLabel: null,
    pipelineStageTone: null,
    internalPipelineStage: null,
    uploadPhase: null,
    runpodJobStatus: null,
    failureReason: null,
    failedAt: null,
    failedStageIndex: null,
    failureDetail: null,
  };
}

/** Drop sticky processing + in-flight UploadContext when a capture is removed. */
export function clearShopifyCaptureClientState(
  productId,
  { captureId, activeCaptureId, activeShopifyProductId, clearUpload } = {},
) {
  removeShopifyProcessingEntry(productId);
  const matchesActiveUpload =
    (activeCaptureId && captureId && activeCaptureId === captureId)
    || (activeShopifyProductId && activeShopifyProductId === productId);
  if (matchesActiveUpload && typeof clearUpload === 'function') {
    clearUpload();
  }
}

function isStickyEntryFresh(entry) {
  const updatedAt = Number(entry?.updatedAt);
  if (!Number.isFinite(updatedAt) || updatedAt <= 0) return false;
  return Date.now() - updatedAt <= STICKY_PROCESSING_MAX_AGE_MS;
}

/**
 * Re-apply processing state only when the catalogue already confirms an in-flight capture.
 * Never promote `none` → `processing` from sessionStorage alone (prevents ghost cards).
 */
export function mergeStickyProcessingIntoProducts(products) {
  const sticky = readStickyProcessingMap();
  const stickyIds = Object.keys(sticky);
  if (!stickyIds.length) return products;

  return products.map((product) => {
    const entry = sticky[product.id];
    if (!entry?.captureId || !entry?.projectId) return product;

    if (product.state === 'live' || product.state === 'failed') {
      removeShopifyProcessingEntry(product.id);
      return product;
    }

    if (!isStickyEntryFresh(entry)) {
      removeShopifyProcessingEntry(product.id);
      return product;
    }

    // Backend says no capture is linked — sticky is stale (e.g. cancelled tab / reload race).
    if (product.state === 'none' && !product.captureId) {
      removeShopifyProcessingEntry(product.id);
      return product;
    }

    if (product.captureId && product.captureId !== entry.captureId) {
      removeShopifyProcessingEntry(product.id);
      return product;
    }

    if (product.state === 'processing') {
      persistShopifyProcessingEntry(product.id, {
        captureId: product.captureId || entry.captureId,
        projectId: product.projectId || entry.projectId,
      });
      return product;
    }

    // Backend already linked this capture but state lagged — keep ids aligned only.
    if (product.captureId === entry.captureId) {
      return {
        ...product,
        projectId: product.projectId || entry.projectId,
      };
    }

    return product;
  });
}

/** Overlay catalogue rows with in-flight UploadContext state (background processing). */
export function mergeActiveUploadIntoProducts(
  products,
  {
    activeShopifyProductId,
    activeCaptureId,
    activeProjectId,
    uploadPhase,
    progressPercent,
    currentStage = null,
  },
) {
  if (!activeShopifyProductId || !activeCaptureId || !isShopifyUploadInProgress(uploadPhase)) {
    return mergeStickyProcessingIntoProducts(products);
  }

  const displayPercent = pickShopifyDisplayPercent({
    uploadContextPercent: progressPercent,
    useUploadContext: true,
    uploadPhase,
  });

  const stage = resolveShopifyPipelineStage({
    uploadPhase,
    progressPercent: displayPercent,
    stageIndex: currentStage,
    processingStatus: uploadPhase === 'queueing' ? 'queued' : 'processing',
  });

  const polledBarLabel = uploadPhase === 'pipeline' && currentStage != null && Number.isFinite(Number(currentStage))
    ? loadingTitleForInternalPipelineStage(Number(currentStage), displayPercent ?? 0)
    : null;

  const pipelineStageLabel = resolveShopifyCardPipelineLabelFromProgress({
    shopifyHeadlineStage: stage,
    pipelineStageLabel: polledBarLabel,
    internalStageIndex: currentStage,
  });

  const merged = products.map((product) => {
    if (product.id !== activeShopifyProductId) return product;
    if (product.state === 'live') return product;

    return {
      ...product,
      state: 'processing',
      captureId: product.captureId || activeCaptureId,
      projectId: product.projectId || activeProjectId,
      uploadPhase,
      runpodJobStatus: product.runpodJobStatus || null,
      progressPercent:
        displayPercent != null
          ? displayPercent
          : product.progressPercent,
      pipelineStageLabel,
      pipelineStageTone: stage.tone,
      internalPipelineStage: currentStage,
    };
  });

  return mergeStickyProcessingIntoProducts(merged);
}

/**
 * Preserve in-memory progress/failure while catalogue polls — never downgrade failed → processing.
 */
export function mergeCatalogueWithLocalProgress(prevProducts, incomingProducts) {
  if (!Array.isArray(incomingProducts)) return [];
  if (!Array.isArray(prevProducts) || !prevProducts.length) return incomingProducts;

  const prevById = new Map(prevProducts.map((product) => [product.id, product]));
  return incomingProducts.map((incoming) => {
    const prev = prevById.get(incoming.id);
    if (!prev) return incoming;

    const sameCapture = Boolean(
      prev.captureId
      && incoming.captureId
      && prev.captureId === incoming.captureId,
    );

    if (prev.state === 'failed' && incoming.state !== 'failed' && sameCapture) {
      return {
        ...incoming,
        state: 'failed',
        failureReason: prev.failureReason ?? incoming.failureReason,
        failedAt: prev.failedAt ?? incoming.failedAt,
        failedStageIndex: prev.failedStageIndex ?? incoming.failedStageIndex,
        failureDetail: prev.failureDetail ?? incoming.failureDetail,
        pipelineStageLabel: null,
        pipelineStageTone: null,
        internalPipelineStage: null,
      };
    }

    if (prev.state === 'processing' && incoming.state === 'processing' && sameCapture) {
      return {
        ...incoming,
        progressPercent: prev.progressPercent ?? incoming.progressPercent,
        pipelineStageLabel: prev.pipelineStageLabel ?? incoming.pipelineStageLabel,
        pipelineStageTone: prev.pipelineStageTone ?? incoming.pipelineStageTone,
        internalPipelineStage: prev.internalPipelineStage ?? incoming.internalPipelineStage,
        runpodJobStatus: prev.runpodJobStatus ?? incoming.runpodJobStatus,
      };
    }

    return incoming;
  });
}
